<template>
  <section
    class="overflow-hidden rounded-xl border border-CLIENT_PRIMARY_PINK bg-[#FDF4F4] p-4 sm:p-5"
    aria-label="アプリダウンロード案内">
    <div class="md:flex md:items-stretch md:gap-6">
      <div class="min-w-0 flex-1">
        <template v-if="weakField">
          <p class="flex items-center gap-1.5 font-bold text-gray-800">
            <svg
              class="h-10 w-10 shrink-0 text-CLIENT_PRIMARY_PINK"
              aria-hidden="true"
              fill="none"
              viewBox="0 0 128 128"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M61.1468 18.7812C62.0449 16.0172 65.9551 16.0172 66.8532 18.7812L75.4502 45.2401C75.8518 46.4762 77.0037 47.3131 78.3034 47.3131H106.124C109.03 47.3131 110.239 51.0319 107.887 52.7401L85.38 69.0927C84.3285 69.8566 83.8886 71.2107 84.2902 72.4468L92.8872 98.9058C93.7853 101.67 90.6218 103.968 88.2707 102.26L65.7634 85.9073C64.7119 85.1434 63.2881 85.1434 62.2366 85.9073L39.7293 102.26C37.3782 103.968 34.2147 101.67 35.1128 98.9058L43.7098 72.4468C44.1114 71.2107 43.6715 69.8566 42.62 69.0927L20.1126 52.7401C17.7615 51.0319 18.9698 47.3131 21.876 47.3131H49.6966C50.9963 47.3131 52.1482 46.4762 52.5498 45.2401L61.1468 18.7812Z"
                fill="currentColor" />
            </svg>
            <span>
              <span class="md:block">今回の伸びしろは</span>
              <span>「{{ weakField.name }}」</span>
            </span>
          </p>
          <p class="mt-2 inline-flex items-center gap-2 rounded-full border border-CLIENT_PRIMARY_PINK bg-white px-3 py-1 text-xs font-bold text-gray-700">
            <span>正答率</span>
            <span class="text-xl font-black leading-none text-CLIENT_PRIMARY_PINK">{{ weakField.personalRate }}%</span>
          </p>
          <p class="mt-4 text-sm leading-6 text-gray-800">
            アプリなら<strong>科目・分野を選んで</strong>苦手な問題を<strong><u>繰り返し演習</u></strong>できます。
          </p>
        </template>
        <p
          v-else
          class="text-sm leading-6 text-gray-700">
          アプリなら<strong>科目・分野を選んで</strong>苦手な問題を<strong><u>繰り返し演習</u></strong>できます。
        </p>

        <div class="mt-4 md:hidden">
          <div class="mb-3 flex items-center justify-center gap-2">
            <img
              class="h-7 w-7 rounded-md"
              src="/images/zoo-icon_sns.png"
              alt="">
            <p class="text-center text-base font-black text-gray-900">どうぶつ看護ポータルアプリ</p>
          </div>
          <a
            v-if="platform === 'ios'"
            :href="iosHandoffUrl || iosStoreUrl"
            class="flex min-h-[48px] items-center justify-center rounded-lg border-2 border-CLIENT_PRIMARY_PINK bg-white px-4 text-sm font-bold text-CLIENT_PRIMARY_PINK shadow-sm transition hover:bg-CLIENT_PRIMARY_PINK hover:text-white active:bg-CLIENT_PRIMARY_PINK_HOVER"
            :target="enabled ? '_self' : '_blank'"
            rel="noopener noreferrer"
            @click="openApp($event, 'ios', iosStoreUrl)">
            App Storeで復習をはじめる
          </a>
          <a
            v-else-if="platform === 'android'"
            :href="androidHandoffUrl || androidStoreUrl"
            class="flex min-h-[48px] items-center justify-center rounded-lg border-2 border-CLIENT_PRIMARY_PINK bg-white px-4 text-sm font-bold text-CLIENT_PRIMARY_PINK shadow-sm transition hover:bg-CLIENT_PRIMARY_PINK hover:text-white active:bg-CLIENT_PRIMARY_PINK_HOVER"
            :target="enabled ? '_self' : '_blank'"
            rel="noopener noreferrer"
            @click="openApp($event, 'android', androidStoreUrl)">
            Google Playで復習をはじめる
          </a>
          <div
            v-else
            class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <a
              :href="iosHandoffUrl || iosStoreUrl"
              class="flex min-h-[48px] items-center justify-center rounded-lg border-2 border-CLIENT_PRIMARY_PINK bg-white px-4 text-sm font-bold text-CLIENT_PRIMARY_PINK shadow-sm transition hover:bg-CLIENT_PRIMARY_PINK hover:text-white active:bg-CLIENT_PRIMARY_PINK_HOVER"
              :target="enabled ? '_self' : '_blank'"
              rel="noopener noreferrer"
              @click="openApp($event, 'ios', iosStoreUrl)">
              iPhone / iPad
            </a>
            <a
              :href="androidHandoffUrl || androidStoreUrl"
              class="flex min-h-[48px] items-center justify-center rounded-lg border-2 border-CLIENT_PRIMARY_PINK bg-white px-4 text-sm font-bold text-CLIENT_PRIMARY_PINK shadow-sm transition hover:bg-CLIENT_PRIMARY_PINK hover:text-white active:bg-CLIENT_PRIMARY_PINK_HOVER"
              :target="enabled ? '_self' : '_blank'"
              rel="noopener noreferrer"
              @click="openApp($event, 'android', androidStoreUrl)">
              Android
            </a>
          </div>
        </div>
      </div>

      <div class="mt-5 hidden shrink-0 border-t border-CLIENT_PRIMARY_PINK pt-4 md:mt-0 md:block md:border-l md:border-t-0 md:pl-6 md:pt-0">
        <div class="flex items-center justify-center gap-2">
          <img
            class="h-7 w-7 rounded-md"
            src="/images/zoo-icon_sns.png"
            alt="">
          <p class="text-center text-base font-black text-gray-900">どうぶつ看護ポータルアプリ</p>
        </div>
        <div class="mt-2 flex justify-center gap-7">
          <div class="text-center">
            <img
              class="mx-auto h-24 w-24"
              :src="iosQrUrl || '/images/AnimalPortal_iOS.png'"
              alt="App StoreへのQRコード">
            <a
              :href="iosHandoffUrl || iosStoreUrl"
              class="mt-1 block text-xs font-bold text-CLIENT_PRIMARY_PINK underline"
              :target="enabled ? '_self' : '_blank'"
              rel="noopener noreferrer"
              @click="openApp($event, 'ios', iosStoreUrl)">iPhone / iPad</a>
          </div>
          <div class="text-center">
            <img
              class="mx-auto h-24 w-24"
              :src="androidQrUrl || '/images/AnimalPortal_Android.png'"
              alt="Google PlayへのQRコード">
            <a
              :href="androidHandoffUrl || androidStoreUrl"
              class="mt-1 block text-xs font-bold text-CLIENT_PRIMARY_PINK underline"
              :target="enabled ? '_self' : '_blank'"
              rel="noopener noreferrer"
              @click="openApp($event, 'android', androidStoreUrl)">Android</a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import {
  computed, onMounted, onBeforeUnmount, ref, watch,
} from 'vue'
import QRCode from 'qrcode'
import getAppPracticeHandoff, {
  buildAppPracticeUrl, clearAppPracticeHandoff, isAppPracticeHandoffAvailable,
} from '@/composables/app-practice-handoff'

// 成績画面からストアへ案内するアプリダウンロードCTAの入力値。
const props = defineProps({
  examinationId: { type: Number, default: null },
  subjectId: { type: Number, default: null },
  enabled: { type: Boolean, default: false },
  weakField: {
    type: Object,
    default: null,
  },
})

const iosStoreUrl = 'https://apps.apple.com/jp/app/%E3%81%A9%E3%81%86%E3%81%B6%E3%81%A4%E7%9C%8B%E8%AD%B7%E3%83%9D%E3%83%BC%E3%82%BF%E3%83%AB/id1628987767'
const androidStoreUrl = 'https://play.google.com/store/apps/details?id=com.vet.animal_portal&hl=ja'
const platform = ref('unknown')

const handoff = ref(null)
const iosQrUrl = ref(null)
const androidQrUrl = ref(null)
const busy = ref(false)
const qrFailed = ref(false)
const iosHandoffUrl = computed(() => (handoff.value ? buildAppPracticeUrl(handoff.value.url, 'ios') : null))
const androidHandoffUrl = computed(() => (handoff.value ? buildAppPracticeUrl(handoff.value.url, 'android') : null))
let generation = 0
let refreshTimer
let statusTimer
let navigationTimer
let unmounted = false

async function prepareHandoff() {
  generation += 1
  const currentGeneration = generation
  clearTimeout(refreshTimer)
  clearTimeout(statusTimer)
  handoff.value = null
  iosQrUrl.value = null
  androidQrUrl.value = null
  qrFailed.value = false
  if (!props.enabled || !props.examinationId || !props.subjectId) return
  const { examinationId, subjectId } = props
  try {
    let data = await getAppPracticeHandoff(examinationId, subjectId)
    if (unmounted || generation !== currentGeneration) return
    // 別科目の表示中に消費されていないか確認し、有効なURLは再利用する。
    const available = await isAppPracticeHandoffAvailable(data.url)
    if (unmounted || generation !== currentGeneration) return
    if (!available) {
      clearAppPracticeHandoff(examinationId, subjectId)
      data = await getAppPracticeHandoff(examinationId, subjectId)
    }
    // 両OSのQRが揃ってから反映し、片方だけ古い科目のQRになることを防ぐ。
    const [iosQr, androidQr] = await Promise.all(['ios', 'android'].map(target => (
      QRCode.toDataURL(buildAppPracticeUrl(data.url, target), { width: 384, margin: 4 })
    )))
    // 科目切替や画面破棄の後に届いた古い応答で、現在のリンクを上書きしない。
    if (unmounted || generation !== currentGeneration) return
    handoff.value = data
    iosQrUrl.value = iosQr
    androidQrUrl.value = androidQr
    // 開いたまま期限を迎えた場合も、新しいURLとQRへ更新する。
    refreshTimer = setTimeout(prepareHandoff, Math.max(1, Date.parse(data.expires_at) - Date.now()))
    // eslint-disable-next-line no-use-before-define
    statusTimer = setTimeout(checkHandoff, 5000)
  } catch {
    if (!unmounted && generation === currentGeneration) qrFailed.value = true
    // APIまたはQR生成に失敗した場合は、既存のストアリンクとQR画像を使用する。
  }
}

async function checkHandoff() {
  const currentGeneration = generation
  const current = handoff.value
  if (!current || unmounted) return
  try {
    const available = await isAppPracticeHandoffAvailable(current.url)
    if (unmounted || generation !== currentGeneration) return
    if (!available) {
      clearAppPracticeHandoff(props.examinationId, props.subjectId)
      await prepareHandoff()
      return
    }
  } catch {
    // 一時的な通信失敗時は現在のQRを維持し、次回再確認する。
  }
  if (!unmounted && generation === currentGeneration) statusTimer = setTimeout(checkHandoff, 5000)
}

async function openApp(event, targetPlatform, storeUrl) {
  if (!props.enabled) return
  event.preventDefault()
  if (busy.value) return
  busy.value = true
  const currentGeneration = generation
  try {
    if (qrFailed.value) {
      window.location.assign(storeUrl)
      return
    }
    const data = handoff.value && Date.parse(handoff.value.expires_at) > Date.now()
      ? handoff.value : await getAppPracticeHandoff(props.examinationId, props.subjectId)
    if (!unmounted && generation === currentGeneration) window.location.assign(buildAppPracticeUrl(data.url, targetPlatform))
  } catch {
    if (!unmounted && generation === currentGeneration) window.location.assign(storeUrl)
  } finally {
    // ページを再読み込みせずストアやアプリから戻った場合も操作できるよう、連打防止を解除する。
    navigationTimer = setTimeout(() => { busy.value = false }, 1000)
  }
}

watch(() => [props.enabled, props.examinationId, props.subjectId], prepareHandoff)
onBeforeUnmount(() => {
  unmounted = true
  generation += 1
  clearTimeout(refreshTimer)
  clearTimeout(statusTimer)
  clearTimeout(navigationTimer)
})

// 端末のOSを判定し、スマートフォンでは対応するストアへのリンクを表示する。
onMounted(() => {
  prepareHandoff()
  const userAgent = navigator.userAgent || ''
  const isIPad = /iPad/.test(userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

  if (/Android/i.test(userAgent)) {
    platform.value = 'android'
  } else if (/iPhone|iPod/i.test(userAgent) || isIPad) {
    platform.value = 'ios'
  }
})
</script>
